import React, { useEffect, useRef, useState } from "react";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Document, Page } from "react-pdf";

const VARIANTS = {
  fiches: {
    backdrop: "modal-backdrop",
    content: "modal-content-pratiques",
    close: "close-btn-pratiques",
    controls: "zoom-controls",
    button: "zoom-button",
    shell: "fiches-pdf-shell",
    scroll: "fiches-pdf-scroll",
    document: "fiches-pdf-document",
    arrow: "fiches-scroll-arrow",
    closeLabel: "X",
  },
  experts: {
    backdrop: "coin-experts-modal-backdrop",
    content: "coin-experts-modal-content",
    close: "coin-experts-close-btn",
    controls: "coin-experts-zoom-controls",
    button: "coin-experts-zoom-button",
    shell: "coin-experts-pdf-shell",
    scroll: "coin-experts-pdf-scroll",
    document: "coin-experts-pdf-document",
    arrow: "coin-experts-scroll-arrow",
    closeLabel: "✕",
  },
};

function PdfModal({ pdfUrl, onClose, variant = "fiches" }) {
  const styles = VARIANTS[variant] || VARIANTS.fiches;
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(1);
  const pdfScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollArrows = () => {
    const container = pdfScrollRef.current;
    if (!container) return;
    setCanScrollLeft(container.scrollLeft > 24);
    setCanScrollRight(container.scrollWidth - container.clientWidth - container.scrollLeft > 24);
  };

  useEffect(() => {
    if (!pdfUrl) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return undefined;
    }

    const handleResize = () => window.requestAnimationFrame(updateScrollArrows);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [pdfUrl, numPages, zoom]);

  if (!pdfUrl) return null;

  const scrollPdfHorizontally = (direction) => {
    const container = pdfScrollRef.current;
    if (!container) return;
    const delta = Math.max(container.clientWidth * 0.75, 240) * direction;
    container.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.content} onClick={(event) => event.stopPropagation()}>
        <button className={styles.close} onClick={onClose} aria-label="Fermer">
          {styles.closeLabel}
        </button>

        <div className={styles.controls}>
          <button
            type="button"
            onClick={() => setZoom((value) => Math.max(value - 0.1, 0.5))}
            className={styles.button}
            aria-label="Dézoomer"
          >
            −
          </button>
          <span>{Math.round(zoom * 100)} %</span>
          <button
            type="button"
            onClick={() => setZoom((value) => Math.min(value + 0.1, 3))}
            className={styles.button}
            aria-label="Zoomer"
          >
            +
          </button>
        </div>

        <div className={styles.shell}>
          <div ref={pdfScrollRef} onScroll={updateScrollArrows} className={styles.scroll}>
            <Document
              className={styles.document}
              file={pdfUrl}
              onLoadSuccess={({ numPages: loadedPages }) => setNumPages(loadedPages)}
            >
              {Array.from({ length: numPages || 0 }, (_, index) => (
                <Page
                  key={`page-${index + 1}`}
                  pageNumber={index + 1}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  scale={zoom}
                />
              ))}
            </Document>
          </div>
          {canScrollLeft && (
            <button
              type="button"
              className={`${styles.arrow} ${styles.arrow}--left`}
              onClick={() => scrollPdfHorizontally(-1)}
              aria-label="Voir la page précédente"
            >
              <KeyboardArrowLeftIcon />
            </button>
          )}
          {canScrollRight && (
            <button
              type="button"
              className={`${styles.arrow} ${styles.arrow}--right`}
              onClick={() => scrollPdfHorizontally(1)}
              aria-label="Voir la page suivante"
            >
              <KeyboardArrowRightIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PdfModal;
